import type { CSSProperties } from 'react';
import { escapeHtml } from './helpers';

export const HERO_OVERLAY_BUTTON_PADDING_DESKTOP = '12px 32px';
export const HERO_OVERLAY_BUTTON_PADDING_MOBILE = '10px 24px';
export const HERO_OVERLAY_BUTTON_FONT_SIZE_DESKTOP = '12px';
export const HERO_OVERLAY_BUTTON_FONT_SIZE_MOBILE = '12px';

export type HeroOverlayButtonConfig = {
  href: string;
  label: string;
  backgroundColor: string;
  textColor: string;
  padding: string;
  fontSize: string;
  fontWeight: string;
  fontFamily: string;
  letterSpacing: string;
  borderRadius: string;
};

export function resolveHeroOverlayButtonConfig(
  content: Record<string, unknown>,
  styles: Record<string, unknown>,
  fontFamily: string,
  isMobile = false
): HeroOverlayButtonConfig {
  const textColor = (styles.color as string) || '#FFFFFF';
  const buttonBg = (content.buttonColor as string) || textColor;
  const buttonTextColor =
    (content.buttonTextColor as string) ||
    (buttonBg === '#FFFFFF' || buttonBg === '#ffffff' ? '#000000' : '#FFFFFF');

  const ff = styles.fontFamily
    ? `'${String(styles.fontFamily).replace(/'/g, '')}', 'Inter', Helvetica, Arial, sans-serif`
    : fontFamily || "'Inter', Helvetica, Arial, sans-serif";

  return {
    href: String(content.buttonUrl || '#'),
    label: String(content.buttonText || ''),
    backgroundColor: buttonBg,
    textColor: buttonTextColor,
    padding: String(
      content.buttonPadding ||
        (isMobile ? HERO_OVERLAY_BUTTON_PADDING_MOBILE : HERO_OVERLAY_BUTTON_PADDING_DESKTOP)
    ),
    fontSize: isMobile ? HERO_OVERLAY_BUTTON_FONT_SIZE_MOBILE : HERO_OVERLAY_BUTTON_FONT_SIZE_DESKTOP,
    fontWeight: '700',
    fontFamily: ff,
    letterSpacing: '0.15em',
    borderRadius: '0',
  };
}

/** Inline styles for the builder canvas — matches exported email button. */
export function getHeroOverlayButtonInlineStyles(
  config: HeroOverlayButtonConfig
): CSSProperties {
  return {
    display: 'inline-block',
    padding: config.padding,
    backgroundColor: config.backgroundColor,
    color: config.textColor,
    textDecoration: 'none',
    fontWeight: config.fontWeight as CSSProperties['fontWeight'],
    borderRadius: config.borderRadius,
    fontSize: config.fontSize,
    fontFamily: config.fontFamily,
    letterSpacing: config.letterSpacing,
    textTransform: 'uppercase',
    boxSizing: 'border-box',
    lineHeight: '1.2',
    border: 'none',
    maxWidth: '100%',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  };
}

type RenderHeroOverlayButtonOptions = {
  align?: 'left' | 'center' | 'right';
  marginTop?: string;
  className?: string;
};

/** Bulletproof email-safe CTA used in preview and sent emails. */
export function renderHeroOverlayButtonHtml(
  config: HeroOverlayButtonConfig,
  options: RenderHeroOverlayButtonOptions = {}
): string {
  const align = options.align || 'left';
  const marginTop = options.marginTop || '32px';
  const wrapClass = options.className || 'editorial-hero-overlay-btn-wrap';
  const tableMargin =
    align === 'center' ? '0 auto' : align === 'right' ? '0 0 0 auto' : '0';

  const anchorStyle = [
    'display: inline-block',
    `background-color: ${config.backgroundColor}`,
    `color: ${config.textColor}`,
    `padding: ${config.padding}`,
    `border-radius: ${config.borderRadius}`,
    `font-size: ${config.fontSize}`,
    `font-weight: ${config.fontWeight}`,
    `font-family: ${config.fontFamily}`,
    'text-decoration: none',
    'text-transform: uppercase',
    `letter-spacing: ${config.letterSpacing}`,
    'line-height: 1.2',
    'border: none',
    'box-sizing: border-box',
    'mso-padding-alt: 0',
    'max-width: 100%',
    'white-space: normal',
    'word-break: break-word',
  ].join('; ');

  return `
    <div class="${wrapClass}" style="margin-top: ${marginTop}; text-align: ${align};">
      <table class="editorial-hero-overlay-btn-table" role="presentation" border="0" cellspacing="0" cellpadding="0" align="${align}" style="margin: ${tableMargin}; width: auto; max-width: 100%;">
        <tr>
          <td align="center" style="border-radius: ${config.borderRadius}; text-align: center;">
            <a href="${escapeHtml(config.href)}" class="editorial-hero-overlay-button" style="${anchorStyle}">${escapeHtml(config.label)}</a>
          </td>
        </tr>
      </table>
    </div>
  `.trim();
}
